<script lang="ts">
    import { _ } from "svelte-i18n"
    import Cart from "../../layout/Cart.svelte"
    import CheckoutFooter from "./CheckoutFooter.svelte"
    import FormInputText from "../../widgets/FormInputText.svelte"
    import FormInputCheckbox from "../../widgets/FormInputCheckbox.svelte"
    import { cart, cartOverlay, checkoutData, login, location, getAsyncCartSum } from "../../../store"
    import { navigate } from "../../../../../vendor/svelte-routing"
    import { getCountry, postOrder } from "../../../api"
    import MiniLoader from "../../widgets/MiniLoader.svelte"
    import PaypalButtons from "./PaypalButtons.svelte"
    import KlarnaButtons from "./KlarnaButtons.svelte"
    import { setUser } from "../../../sentry"
    import { maxInputLength } from "../../../../../api/hooks/config-client"

    let cancellationNotice = false
    let privacyNotice = false
    let conditionsNotice = false
    let loading = false

    location.subscribe((l) => {
        if (l?.path == "/checkout/overview") {
            // close overlay because cart is also in this overview
            $cartOverlay = false
        }
    })

    $: {
        if (!$cart?.length || $login) {
            // empty cart goto start
            navigate("/", { replace: true })
        } else if ($checkoutData.stepsValid < 1) {
            navigate("/checkout", { replace: true })
        } else if ($checkoutData.stepsValid < 2) {
            // incomplete data
            navigate("/checkout/payment", { replace: true })
        }
    }

    let paymentName: string
    $: switch ($checkoutData.payment.type) {
        case "paypal":
            paymentName = "Paypal"
            break
        case "invoice":
            paymentName = $_("checkout.invoice")
            break
    }

    let deliveryCountry: Country
    let cartSum: CartSum

    async function _getCartSum(_cart: CartItem[]) {
        deliveryCountry = await getCountry($checkoutData.deliveryAddress.countryCode)
        cartSum = await getAsyncCartSum(_cart, deliveryCountry)
    }

    $: _getCartSum($cart)

    const submitOrder = async () => {
        try {
            // fix email input binding with spaces
            $checkoutData.contact.email = $checkoutData.contact.email.trim()

            // in sentry
            setUser({
                email: $checkoutData.contact.email,
            })

            // restrict contact, deliveryAddress and billingAddress object to max length
            for (const key in $checkoutData.contact) {
                // @ts-ignore
                if ($checkoutData.contact[key]?.length > maxInputLength[key]) {
                    // @ts-ignore
                    $checkoutData.contact[key] = $checkoutData.contact[key].substring(0, maxInputLength[key])
                }
            }
            for (const key in $checkoutData.deliveryAddress) {
                // @ts-ignore
                if ($checkoutData.deliveryAddress[key]?.length > maxInputLength[key]) {
                    // @ts-ignore
                    $checkoutData.deliveryAddress[key] = $checkoutData.deliveryAddress[key].substring(
                        0,
                        // @ts-ignore
                        maxInputLength[key]
                    )
                }
            }
            for (const key in $checkoutData.billingAddress) {
                // @ts-ignore
                if ($checkoutData.billingAddress[key]?.length > maxInputLength[key]) {
                    // @ts-ignore
                    $checkoutData.billingAddress[key] = $checkoutData.billingAddress[key].substring(
                        0,
                        // @ts-ignore
                        maxInputLength[key]
                    )
                }
            }

            const result = await postOrder({
                contact: $checkoutData.contact,
                deliveryAddress: $checkoutData.deliveryAddress,
                deliveryCountry: deliveryCountry,
                billingAddress: $checkoutData.billingAddress,
                sameAddress: $checkoutData.sameAddress,
                vat: $checkoutData.vat,
                sum: cartSum,
                payment: $checkoutData.payment,
                cart: $cart,
            })

            typeof window !== "undefined" &&
                window.setTimeout(() => {
                    // clear
                    $checkoutData.stepsValid = 0
                    $cart = []
                    // $pppApproval = { url: "", total: 0 }
                    $checkoutData.payment = { type: $checkoutData.payment.type }
                }, 1000)

            navigate("/checkout/confirmation")
        } catch (e) {
            console.log(e)
            alert($_("checkout.error-alert"))
            $checkoutData.stepsValid = 0
            throw new Error(JSON.stringify(e, null, 4))
        }
    }

    function submitClick() {
        if (!loading) {
            loading = true
            submitOrder()
        }
    }

    $: {
        console.log($checkoutData.payment)

        // goto payment choice if not selected
        if (!$checkoutData.payment.type) {
            navigate("/checkout/payment", { replace: true })
        }
    }

    $: if (cartSum?.totalGross > 500 && $checkoutData.payment.type == "invoice") {
        navigate("/checkout/payment", { replace: true })
    }
</script>

<div class="subline">{$_("checkout.order")}</div>
<h2 class="h2_nooffset">{$_("checkout.summary")}</h2>

<form on:submit|preventDefault="{submitClick}">
    <section>
        <div class="section_headline">{$_("checkout.cart")}</div>
        <Cart checkoutButton="{false}" />
    </section>
    <section>
        <div class="section_headline">{$_("checkout.contact-data")}</div>
        <div class="row">
            <div class="col-md-12">
                <FormInputText
                    name="email"
                    placeholder="{$_('checkout.email')}"
                    label="{$_('checkout.email')}"
                    disabled
                    value="{$checkoutData.contact.email}"
                />
            </div>
            <div class="col-md-12">
                <FormInputText
                    name="phone"
                    placeholder="{$_('checkout.phone')}"
                    label="{$_('checkout.phone')}"
                    disabled
                    value="{$checkoutData.contact.phone}"
                />
            </div>
        </div>
    </section>
    <section>
        <div class="section_headline">{$_("checkout.shipping-address")}</div>
        <div class="row">
            <div class="col-md-6">
                <FormInputText
                    name="delivery_firstname"
                    placeholder="{$_('checkout.firstname')}"
                    label="{$_('checkout.firstname')}"
                    disabled
                    value="{$checkoutData.deliveryAddress.firstname}"
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="delivery_lastname"
                    placeholder="{$_('checkout.lastname')}"
                    label="{$_('checkout.lastname')}"
                    disabled
                    value="{$checkoutData.deliveryAddress.lastname}"
                />
            </div>
            <div class="col-md-10">
                <FormInputText
                    name="delivery_street"
                    placeholder="{$_('checkout.street')}"
                    label="{$_('checkout.street')}"
                    disabled
                    value="{$checkoutData.deliveryAddress.street}"
                />
            </div>
            <div class="col-md-2">
                <FormInputText
                    name="delivery_houseNumber"
                    placeholder="{$_('checkout.house-number')}"
                    label="{$_('checkout.house-number')}"
                    disabled
                    value="{$checkoutData.deliveryAddress.houseNumber}"
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="delivery_postcode"
                    placeholder="{$_('checkout.postcode')}"
                    label="{$_('checkout.postcode')}"
                    disabled
                    value="{$checkoutData.deliveryAddress.postcode}"
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="delivery_city"
                    placeholder="{$_('checkout.city')}"
                    label="{$_('checkout.city')}"
                    disabled
                    value="{$checkoutData.deliveryAddress.city}"
                />
            </div>
            <div class="col-md-12">
                <FormInputText
                    name="delivery_country"
                    placeholder="{$_('checkout.country')}"
                    label="{$_('checkout.country')}"
                    disabled
                    value="{$checkoutData.deliveryAddress.countryCode}"
                />
            </div>
        </div>
    </section>
    <section>
        <div class="section_headline">{$_("checkout.billing-address")}</div>
        <div class="row">
            <div class="col-md-6">
                <FormInputText
                    name="billing_firstname"
                    placeholder="{$_('checkout.firstname')}"
                    label="{$_('checkout.firstname')}"
                    disabled
                    value="{$checkoutData.billingAddress.firstname}"
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="billing_lastname"
                    placeholder="{$_('checkout.lastname')}"
                    label="{$_('checkout.lastname')}"
                    disabled
                    value="{$checkoutData.billingAddress.lastname}"
                />
            </div>
            <div class="col-md-10">
                <FormInputText
                    name="billing_street"
                    placeholder="{$_('checkout.street')}"
                    label="{$_('checkout.street')}"
                    disabled
                    value="{$checkoutData.billingAddress.street}"
                />
            </div>
            <div class="col-md-2">
                <FormInputText
                    name="billing_houseNumber"
                    placeholder="{$_('checkout.house-number')}"
                    label="{$_('checkout.house-number')}"
                    disabled
                    value="{$checkoutData.billingAddress.houseNumber}"
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="billing_postcode"
                    placeholder="{$_('checkout.postcode')}"
                    label="{$_('checkout.postcode')}"
                    disabled
                    value="{$checkoutData.billingAddress.postcode}"
                />
            </div>
            <div class="col-md-6">
                <FormInputText
                    name="billing_city"
                    placeholder="{$_('checkout.city')}"
                    label="{$_('checkout.city')}"
                    disabled
                    value="{$checkoutData.billingAddress.city}"
                />
            </div>
            <div class="col-md-12">
                <FormInputText
                    name="billing_country"
                    placeholder="{$_('checkout.country')}"
                    label="{$_('checkout.country')}"
                    disabled
                    value="{$checkoutData.billingAddress.countryCode}"
                />
            </div>
        </div>
    </section>

    <section style="border-bottom:dotted 1px var(--border-color-2);">
        <div class="section_headline">{$_("checkout.payment")}</div>
        <p>{paymentName}</p>
    </section>

    <section>
        <FormInputCheckbox label="{$_('checkout.store-my-data')}" bind:checked="{$checkoutData.localStorage}" />
        <FormInputCheckbox label="" bind:checked="{cancellationNotice}">
            {@html $_("checkout.right-of-withdrawal-checkbox-html")}
        </FormInputCheckbox>
        <FormInputCheckbox label="" bind:checked="{privacyNotice}">
            {@html $_("checkout.privacy-terms-checkbox-html")}
        </FormInputCheckbox>
        <FormInputCheckbox label="" bind:checked="{conditionsNotice}">
            {@html $_("checkout.general-terms-checkbox-html")}
        </FormInputCheckbox>
    </section>
    {#if loading}
        <MiniLoader />
    {:else}
        <CheckoutFooter
            backlink="/checkout/payment"
            backtitle="{$_('checkout.back-to-payment')}"
            nexttitle="{$_('checkout.order-now')}"
            nextComponent="{{ paypal: PaypalButtons, klarna: KlarnaButtons, invoice: null }[
                $checkoutData.payment.type
            ]}"
            submitDisabled="{!cancellationNotice ||
                !privacyNotice ||
                !conditionsNotice ||
                !deliveryCountry ||
                !cartSum}"
            submitHandler="{submitClick}"
        />
    {/if}
</form>
